/*
 * The Foozie PDP (session A3, 2026-09-24). Three small elements, one file.
 *
 * WHY THE BUY BOX HAS ALMOST NO JAVASCRIPT. Horizon's variant picker already
 * fetches the whole page again for every swatch or size tap (?option_values=,
 * rendered with that variant selected) and hands the parsed HTML to anything
 * listening for Shopify's `product:select` standard event. So every Foozie
 * block decides its state in Liquid -- sold out or not, which pre-order date,
 * whether the express box shows -- and <fz-sync> only swaps its own subtree
 * for the matching one in that HTML. There is one definition of each rule
 * (the Liquid), not a server copy and a client copy that can drift, which is
 * what the control's pre-order callout suffered from (W-25).
 *
 * BUT A FETCH IS NOT INSTANT, and Luke felt it (26 Sep): switching "takes a
 * very long time and doesn't feel as smooth" as live, which swaps in the
 * browser. That fetch is the WHOLE page (no section_id for the main product),
 * parsed with DOMParser, before anything the shopper looks at changes. So
 * the blocks whose state a shopper watches (the price, the button, the
 * sticky bar, the ship date) also print, from the SAME snippets, a small
 * per-variant map (<script data-fz-instant>), and showVariant() below puts
 * the chosen variant's entries on the page in the tap's own task, with the
 * gallery moved to its picture and the URL updated. The fetch still runs and
 * lands behind it; because the markup is the same, <fz-sync> and the sticky
 * bar find nothing to change, and no fade is added over a picture already
 * shown. The Liquid is still the one definition of each rule.
 *
 * INP: the tap's task does the swap from the parsed maps (cached per script
 * element) and a handful of DOM writes; the fetch's work lands in a later
 * task, and the crossfade is transform/opacity only.
 */
import { StandardEvents } from "@shopify/events";
import { morph, MORPH_OPTIONS } from "@theme/morph";

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)";

/**
 * Listen for product:select on the closest section, and give each resolved
 * page to `onPage`. Aborted fetches (a second tap before the first landed)
 * are the picker working as designed, not errors.
 * @param {HTMLElement} host
 * @param {AbortSignal} signal
 * @param {(html: Document, detail: any) => void} onPage
 */
function onProductPage(host, signal, onPage) {
  const section = host.closest(".shopify-section");
  if (!section) return;
  section.addEventListener(
    StandardEvents.productSelect,
    (/** @type {any} */ event) => {
      if (!(event.target instanceof Element) || event.target.closest("product-card")) return;
      event.promise
        .then((/** @type {any} */ result) => {
          const detail = result && result.detail;
          if (!detail || !detail.html) return;
          if (detail.productId && host.dataset.productId && detail.productId !== host.dataset.productId) return;
          onPage(detail.html, detail);
        })
        .catch((/** @type {any} */ error) => {
          if (error && error.name !== "AbortError") console.warn("[fz-product]", error);
        });
    },
    { signal },
  );
}

/**
 * <fz-sync data-key="..."> re-renders itself from the page the variant picker
 * fetched. data-fz-animate crossfades the new content in when it changed (the
 * price and the per-night pill), so a price that moves is seen to move.
 */
class FzSync extends HTMLElement {
  #controller = new AbortController();

  connectedCallback() {
    onProductPage(this, this.#controller.signal, (html) => {
      const next = html.querySelector(`fz-sync[data-key="${this.dataset.key}"]`);
      if (!next) return;
      if (next.innerHTML === this.innerHTML) return;
      const before = this.textContent;
      // The new state is ON the new element too (hidden, data attributes).
      for (const name of ["hidden", "data-state"]) {
        if (next.hasAttribute(name)) this.setAttribute(name, next.getAttribute(name) || "");
        else this.removeAttribute(name);
      }
      morph(this, next, MORPH_OPTIONS);
      if (this.hasAttribute("data-fz-animate") && before !== this.textContent && !reducedMotion()) {
        this.animate(
          [
            { opacity: 0.35, transform: "translateY(2px)", filter: "blur(2px)" },
            { opacity: 1, transform: "none", filter: "blur(0)" },
          ],
          { duration: 200, easing: EASE_OUT },
        );
      }
      this.dispatchEvent(new CustomEvent("fz:synced", { bubbles: true }));
    });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }
}

/**
 * The sticky bar's inputs from two rectangles and the window height. Pure, so
 * bin/test-fz-sticky.mjs can drive it. No button (none rendered): treated as
 * visible, so the bar never shows. The footer counts while any of it is on
 * screen or within 120 px above the top, as the observer's rootMargin did.
 * @param {{ top: number, bottom: number } | undefined} button
 * @param {{ top: number, bottom: number } | undefined} footer
 * @param {number} viewportHeight
 */
export function stickyState(button, footer, viewportHeight) {
  const buttonVisible = !button || (button.bottom > 0 && button.top < viewportHeight);
  // Strictly below 0: a main button that is not rendered (display: none, as on
  // a sold-out default variant) reads as a 0 x 0 rect at the origin, and "<= 0"
  // called that "scrolled above", which put the bar over the first screen of
  // the Move PDP at 768 (the post-merge sweep, V-157). The observer this
  // replaced never counted it as above either.
  const buttonAbove = !!button && button.bottom < 0;
  const footerVisible = !!footer && footer.top < viewportHeight && footer.bottom > -120;
  return { buttonVisible, buttonAbove, footerVisible };
}

/**
 * How far up the sticky bar must stand to clear an app's launcher in the
 * window's bottom-right corner (an app's chat or rewards button, where the app
 * places it there): the distance from the window's bottom
 * to the top of the highest one under the bar's span, plus 12 px; 0 if none.
 * A launcher is a FIXED box the page did not draw: a child or grandchild of
 * <body>, outside .page-wrapper, smaller than half the window each way (so a
 * backdrop or a full-width banner never counts), visible, in the bottom third.
 * @param {HTMLElement} bar  the bar, for its width (offsetWidth ignores the slide)
 * @param {number} right     where the bar's right edge goes, from the left of the window
 * @param {number} viewportHeight
 */
function launcherLift(bar, right, viewportHeight) {
  const left = right - bar.offsetWidth;
  const viewportWidth = document.documentElement.clientWidth;
  let top = viewportHeight;
  const boxes = [...document.body.children].flatMap((el) => [el, ...el.children]);
  for (const el of boxes) {
    if (el.closest(".page-wrapper, fz-sticky-atc, dialog, script, style, template")) continue;
    const style = getComputedStyle(el);
    if (
      style.position !== "fixed" ||
      style.visibility === "hidden" ||
      style.display === "none" ||
      style.opacity === "0"
    )
      continue;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height || r.width > viewportWidth / 2 || r.height > viewportHeight / 2) continue;
    if (r.right <= left || r.left >= right || r.bottom <= viewportHeight * (2 / 3)) continue;
    top = Math.min(top, r.top);
  }
  return top < viewportHeight ? Math.ceil(viewportHeight - top + 12) : 0;
}

/**
 * The sticky add-to-cart bar. It never changes the variant itself: it names
 * the colour and size the picker has selected and adds exactly that, by
 * pressing the main button (so the cart, the drawer and Horizon's cart events
 * run one code path). Tapping the variant name scrolls back to the picker.
 *
 * Shown whenever the main button is off screen on a phone (above OR below:
 * the control's bar is always there on a phone, and a first screen that is
 * all gallery should still offer the button), and only once it has been
 * scrolled past on a wider screen, where the buy box sits beside the gallery.
 * Never while the main button is visible, so the coral appears once per
 * screen, and never over the footer.
 *
 * WHERE THE BUTTON IS comes from its rectangle, read once per frame while
 * anything scrolls, not from IntersectionObserver entries. An observer only
 * reports a CROSSING, so a jump that carries the button from below the screen
 * to above it without passing through (the stars link to the reviews, a
 * restored scroll, the capture tool's scroll-to-bottom-and-back) left "above"
 * stale: the bar showed over the thumbnails at the top of the page at 1280
 * (V-094, reproduced on the 2026-09-25 proof). Scroll is listened for on the
 * document in the capture phase, because on a wide screen Horizon scrolls
 * .page-wrapper, not the window, and scroll events do not bubble.
 *
 * From 990 px it sits in the window's bottom-right corner, at its own width
 * (Luke, mother chat, 9 Oct 2026; the CSS has his words). Until then it sat
 * over the info column (V-094), which lower on the page is long gone. Two
 * things the CSS cannot know are measured when the bar appears and on resize:
 * the width of .page-wrapper's scrollbar, so the bar's 24 px are from the
 * scrollbar and not under it (--fz-sticky-scrollbar), and an app's launcher
 * in that corner (--fz-sticky-lift): an app's launcher sits above any z-index
 * of ours, so in the corner it would cover ADD TO CART, and the bar steps up
 * above it instead. A GUARD: none is there on a PDP today (Tolstoy's bubble is
 * set never to show on product pages, Luke, 10 Oct 2026).
 */
class FzStickyAtc extends HTMLElement {
  #controller = new AbortController();
  /** @type {Element | null} */
  #footer = null;
  #frame = 0;
  #buttonVisible = true;
  #buttonAbove = false;
  #footerVisible = false;
  #narrow = window.matchMedia("(max-width: 749px)");
  #wide = window.matchMedia("(min-width: 990px)");
  #placeFrame = 0;

  connectedCallback() {
    const { signal } = this.#controller;
    this.#footer = document.querySelector("footer") || document.querySelector('[class*="footer-group"]');
    document.addEventListener("scroll", this.#schedule, { signal, capture: true, passive: true });
    window.addEventListener("resize", this.#schedule, { signal, passive: true });
    this.#schedule();
    this.#narrow.addEventListener("change", () => this.#update(), { signal });
    this.#wide.addEventListener("change", () => this.#place(), { signal });
    window.addEventListener(
      "resize",
      () => {
        if (this.#placeFrame) return;
        this.#placeFrame = requestAnimationFrame(() => {
          this.#placeFrame = 0;
          this.#place();
        });
      },
      { signal, passive: true },
    );
    this.#place();

    this.addEventListener("click", this.#onClick, { signal });
    this.addEventListener("change", this.#onQuantityTyped, { signal });

    // The quantity box is the buy box's stepper's twin (Luke, 23 Oct 2026):
    // whatever changes the buy box's quantity, the box reads it back.
    const section = this.closest(".shopify-section");
    section?.addEventListener("quantity-selector:update", () => this.#syncQuantity(), { signal });
    section?.addEventListener(
      "input",
      (event) => {
        if (event.target === this.#mainQuantity()) this.#syncQuantity();
      },
      { signal },
    );
    this.#syncQuantity();

    onProductPage(this, signal, (html) => {
      const next = html.querySelector("fz-sticky-atc");
      const bar = this.querySelector(".fz-sticky__bar");
      const nextBar = next && next.querySelector(".fz-sticky__bar");
      if (!next || !bar || !nextBar) return;
      morph(bar, nextBar, MORPH_OPTIONS);
      this.dataset.variantAvailable = next.getAttribute("data-variant-available") || "false";
      // The morph brings the server's 1; the buy box holds the quantity.
      this.#syncQuantity();
    });
  }

  /** The buy box's own quantity input, the one the add sends. */
  #mainQuantity() {
    const input = this.#mainButtonArea()?.querySelector('quantity-selector-component input[type="number"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** The box shows the buy box's quantity and its limits; with no stepper in the buy box, it is hidden. */
  #syncQuantity() {
    const box = /** @type {HTMLElement | null} */ (this.querySelector(".fz-sticky__qty"));
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector(".fz-sticky__qty-input"));
    if (!box || !input) return;
    const main = this.#mainQuantity();
    box.hidden = !main;
    if (!main) return;
    if (input.value !== main.value) input.value = main.value;
    input.disabled = main.disabled;
    for (const [step, selector] of [
      ["-1", ".quantity-minus"],
      ["1", ".quantity-plus"],
    ]) {
      const twin = /** @type {HTMLButtonElement | null} */ (this.querySelector(`[data-fz-sticky-step="${step}"]`));
      const own = /** @type {HTMLButtonElement | null | undefined} */ (
        this.#mainButtonArea()?.querySelector(`quantity-selector-component ${selector}`)
      );
      if (twin && own) twin.disabled = own.disabled || main.disabled;
    }
  }

  /** A number typed in the box goes through the buy box's own rules (Horizon's blur: min, max, step). @param {Event} event */
  #onQuantityTyped = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || !input.classList.contains("fz-sticky__qty-input")) return;
    const main = this.#mainQuantity();
    if (!main) return;
    main.value = input.value;
    main.dispatchEvent(new FocusEvent("blur"));
    this.#syncQuantity();
  };

  disconnectedCallback() {
    this.#controller.abort();
    if (this.#frame) cancelAnimationFrame(this.#frame);
  }

  #schedule = () => {
    if (!this.#frame) this.#frame = requestAnimationFrame(this.#measure);
  };

  /** Where the main button and the footer are now, from their rectangles. */
  #measure = () => {
    this.#frame = 0;
    const next = stickyState(
      this.#mainButtonArea()?.getBoundingClientRect(),
      this.#footer?.getBoundingClientRect(),
      window.innerHeight,
    );
    this.#buttonVisible = next.buttonVisible;
    this.#buttonAbove = next.buttonAbove;
    this.#footerVisible = next.footerVisible;
    this.#update();
  };

  #mainButtonArea() {
    const section = this.closest(".shopify-section");
    return section && section.querySelector(".fz-buy-buttons");
  }

  #update() {
    const show = !this.#buttonVisible && !this.#footerVisible && (this.#narrow.matches || this.#buttonAbove);
    if (show && this.dataset.stuck !== "true") this.#place();
    this.dataset.stuck = show ? "true" : "false";
  }

  /** From 990 px: clear of the scrollbar and of an app's corner launcher (the CSS does the rest). */
  #place() {
    if (!this.#wide.matches) {
      this.style.removeProperty("--fz-sticky-scrollbar");
      this.style.removeProperty("--fz-sticky-lift");
      return;
    }
    const wrapper = document.querySelector(".page-wrapper");
    const scrollbar = wrapper instanceof HTMLElement ? Math.max(0, wrapper.offsetWidth - wrapper.clientWidth) : 0;
    this.style.setProperty("--fz-sticky-scrollbar", `${scrollbar}px`);
    const bar = /** @type {HTMLElement | null} */ (this.querySelector(".fz-sticky__bar"));
    const lift = bar ? launcherLift(bar, document.documentElement.clientWidth - scrollbar - 24, window.innerHeight) : 0;
    if (lift) this.style.setProperty("--fz-sticky-lift", `${lift}px`);
    else this.style.removeProperty("--fz-sticky-lift");
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const el = /** @type {Element} */ (event.target);
    const step = /** @type {HTMLElement | null} */ (el.closest("[data-fz-sticky-step]"));
    if (step) {
      event.preventDefault();
      const own = this.#mainButtonArea()?.querySelector(
        `quantity-selector-component ${step.dataset.fzStickyStep === "1" ? ".quantity-plus" : ".quantity-minus"}`,
      );
      if (own instanceof HTMLButtonElement && !own.disabled) own.click();
      this.#syncQuantity();
      return;
    }
    if (el.closest("[data-fz-sticky-jump]")) {
      event.preventDefault();
      const picker = this.closest(".shopify-section")?.querySelector(".fz-variants");
      picker?.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "center" });
      picker?.querySelector("input:checked")?.focus({ preventScroll: true });
      return;
    }
    const button = el.closest(".fz-sticky__button");
    if (!button || /** @type {HTMLButtonElement} */ (button).disabled) return;
    const main = /** @type {HTMLButtonElement | null | undefined} */ (
      this.#mainButtonArea()?.querySelector('[ref="addToCartButton"]')
    );
    if (!main || main.disabled) return;
    // Horizon's own flag: the main button skips its fly and burst, because
    // the shopper pressed THIS one.
    main.dataset.puppet = "true";
    main.click();
    main.dataset.puppet = "false";
    this.#fly(/** @type {HTMLElement} */ (button));
    button.setAttribute("data-added", "true");
    window.setTimeout(() => button.removeAttribute("data-added"), 1200);
  };

  /** Horizon's fly-to-cart, launched from this button with the variant's picture. @param {HTMLElement} source */
  #fly(source) {
    if (reducedMotion()) return;
    const cartIcon = document.querySelector(".header-actions__cart-icon");
    const container = this.#mainButtonArea()?.querySelector("add-to-cart-component");
    const image = container instanceof HTMLElement ? container.dataset.productVariantMedia : "";
    if (!cartIcon || !image || !customElements.get("fly-to-cart")) return;
    const fly = /** @type {any} */ (document.createElement("fly-to-cart"));
    fly.classList.add("fly-to-cart--main");
    fly.style.setProperty("background-image", `url(${image})`);
    fly.style.setProperty("--start-opacity", "0");
    fly.source = source;
    fly.destination = cartIcon;
    document.body.appendChild(fly);
  }
}

/**
 * The size-guide drawer: a native modal <dialog>, so Esc, the focus trap and
 * the inert page behind it are the browser's. It slides up on a phone and in
 * from the right on a wide screen (CSS, @starting-style), closes on a tap
 * outside the panel, and hands focus back to the link that opened it.
 */
class FzDrawer extends HTMLElement {
  /** @type {HTMLElement | null} */
  #opener = null;
  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const dialog = this.dialog;
    if (!dialog) return;
    document.addEventListener(
      "click",
      (event) => {
        const name = this.dataset.drawer || this.id;
        const trigger = /** @type {Element} */ (event.target).closest(`[data-fz-drawer-open="${name}"]`);
        if (!trigger) return;
        event.preventDefault();
        this.open(/** @type {HTMLElement} */ (trigger));
      },
      { signal },
    );
    dialog.addEventListener(
      "click",
      (event) => {
        const el = /** @type {Element} */ (event.target);
        if (el === dialog || el.closest("[data-fz-drawer-close]")) this.close();
      },
      { signal },
    );
    // Escape: the same animated exit as the close button, not an instant close.
    // (Where the browser will not let `cancel` be prevented, it simply closes.)
    dialog.addEventListener(
      "cancel",
      (event) => {
        event.preventDefault();
        this.close();
      },
      { signal },
    );
    dialog.addEventListener("close", () => this.#restoreFocus(), { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get dialog() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector("dialog"));
  }

  /** @param {HTMLElement} [opener] */
  open(opener) {
    const dialog = this.dialog;
    if (!dialog) return;
    if (dialog.open) {
      // Reopened mid-exit: stay open, and let the next close run.
      this.#closing?.abort();
      this.#closing = null;
      dialog.removeAttribute("data-closing");
      return;
    }
    this.#opener = opener || /** @type {HTMLElement | null} */ (document.activeElement);
    dialog.showModal();
    document.documentElement.classList.add("fz-drawer-open");
  }

  /** @type {AbortController | null} */
  #closing = null;

  /**
   * The exit runs while the dialog is still open and in the top layer
   * ([data-closing] in fz-product.css), and only then does dialog.close()
   * take it out. Animating the CLOSED state needs CSS `overlay` to stay in the
   * top layer, and iOS has none, so the sheet flashed over the buy box there
   * (A7's IA-03). The timer is the fallback for a transition that never
   * reports its end (reduced motion off in CSS, a hidden tab).
   */
  close() {
    const dialog = this.dialog;
    if (!dialog || !dialog.open || this.#closing) return;
    const closing = new AbortController();
    this.#closing = closing;
    dialog.setAttribute("data-closing", "");
    const finish = () => {
      if (closing.signal.aborted) return;
      closing.abort();
      this.#closing = null;
      dialog.removeAttribute("data-closing");
      dialog.close();
    };
    dialog.addEventListener(
      "transitionend",
      (event) => {
        if (event.target === dialog) finish();
      },
      { signal: closing.signal },
    );
    window.setTimeout(finish, 320);
  }

  #restoreFocus() {
    document.documentElement.classList.remove("fz-drawer-open");
    this.#opener?.focus({ preventScroll: true });
    this.#opener = null;
  }
}

for (const [name, klass] of /** @type {[string, CustomElementConstructor][]} */ ([
  ["fz-sync", FzSync],
  ["fz-sticky-atc", FzStickyAtc],
  ["fz-drawer", FzDrawer],
])) {
  if (!customElements.get(name)) customElements.define(name, klass);
}

/* ---------------------------------------------------------------------------
 * INSTANT VARIANTS (Luke, 26 Sep). See the note at the top of this file.
 * ------------------------------------------------------------------------ */

/** @type {WeakMap<Element, any>} */
const parsedMaps = new WeakMap();

/**
 * A block's per-variant map, parsed once per <script> element.
 * @param {ParentNode} root
 * @param {string} name
 */
export function instantMap(root, name) {
  const script = root.querySelector(`script[data-fz-instant="${name}"]`);
  if (!script) return null;
  if (!parsedMaps.has(script)) {
    let value = null;
    try {
      value = JSON.parse(script.textContent || "null");
    } catch {
      value = null;
    }
    parsedMaps.set(script, value);
  }
  return parsedMaps.get(script);
}

/**
 * The variant the picker's checked values name, from its map. Pure over its
 * inputs, so bin/test-fz-instant.mjs can drive it.
 * @param {{ id: number, options: string[], media: number | null }[] | null} variants
 * @param {(string | undefined)[]} chosen - one value per option, in option order
 */
export function findVariant(variants, chosen) {
  if (!Array.isArray(variants) || !chosen.length || chosen.some((value) => value == null)) return null;
  return (
    variants.find(
      (variant) =>
        variant.options.length === chosen.length && variant.options.every((option, i) => option === chosen[i]),
    ) || null
  );
}

/**
 * The picker's option controls in the options' own order: a <fieldset> of
 * radios (the colour, the gift card's value) or a <select> (the size, a
 * dropdown as on Debut since 23 Oct 2026).
 * @param {Element} picker
 * @returns {Element[]}
 */
export function optionControls(picker) {
  return [...picker.querySelectorAll("fieldset, select")];
}

/**
 * The chosen value of each option, in the options' order: the checked radio
 * of a fieldset, or a select's value.
 * @param {Element} picker
 * @returns {(string | undefined)[]}
 */
export function chosenValues(picker) {
  return optionControls(picker).map((control) =>
    control instanceof HTMLSelectElement
      ? control.value
      : /** @type {HTMLInputElement | null} */ (control.querySelector("input:checked"))?.value,
  );
}

/** @param {Element} element */
function fadeIn(element) {
  if (reducedMotion()) return;
  element.animate(
    [
      { opacity: 0.35, transform: "translateY(2px)", filter: "blur(2px)" },
      { opacity: 1, transform: "none", filter: "blur(0)" },
    ],
    { duration: 200, easing: EASE_OUT },
  );
}

/**
 * The button's words: its one text node that is not whitespace (Horizon
 * prints the icon, the words, then the added-tick span).
 * @param {Element} button
 * @param {string} label
 */
function setButtonWords(button, label) {
  const node = [...button.childNodes].find(
    (child) => child.nodeType === Node.TEXT_NODE && (child.textContent || "").trim(),
  );
  if (node) {
    if (node.textContent !== label) node.textContent = label;
  } else {
    button.insertBefore(document.createTextNode(label), button.querySelector(".add-to-cart__added"));
  }
}

/**
 * Put the chosen variant on the page at once, from the blocks' maps.
 * -> true when its picture is now the one shown (so no fade is needed when
 * the fetched gallery replaces this one, opening on the same picture).
 * @param {Element} section
 */
export function showVariant(section) {
  const picker = section.querySelector("variant-picker");
  if (!picker) return false;
  const chosen = chosenValues(picker);
  const variant = findVariant(instantMap(picker, "variants"), chosen);
  if (!variant) return false;
  const id = String(variant.id);

  const price = instantMap(section, "price")?.[id];
  if (price) {
    const box = section.querySelector("fz-sync.fz-price");
    if (box && box.innerHTML !== price.html) {
      const before = box.textContent;
      box.innerHTML = price.html;
      if (box.textContent !== before) fadeIn(box);
    }
    const night = section.querySelector("[data-fz-night]");
    if (night && night.textContent !== price.night) {
      night.textContent = price.night;
      fadeIn(night.closest("fz-sync") || night);
    }
  }

  const atc = instantMap(section, "atc")?.[id];
  if (atc) {
    const form = section.querySelector("product-form-component");
    const idInput = /** @type {HTMLInputElement | null} */ (form && form.querySelector('input[name="id"]'));
    if (idInput) idInput.value = id;
    const button = /** @type {HTMLButtonElement | null} */ (form && form.querySelector('[ref="addToCartButton"]'));
    if (button) {
      button.disabled = !atc.can;
      setButtonWords(button, atc.label);
      const icon = /** @type {HTMLElement | null} */ (button.querySelector(".add-to-cart-icon"));
      if (icon) icon.hidden = !atc.can;
    }
    for (const bundle of section.querySelectorAll("fz-sync.fz-bundle")) {
      /** @type {HTMLElement} */ (bundle).hidden = !atc.can;
    }
  }

  const sticky = section.querySelector("fz-sticky-atc");
  const bar = sticky && instantMap(sticky, "sticky")?.[id];
  if (sticky && bar) {
    /** @type {HTMLElement} */ (sticky).dataset.variantAvailable = String(bar.can);
    const link = sticky.querySelector(".fz-sticky__variant");
    if (link && link.innerHTML !== bar.link) link.innerHTML = bar.link;
    const line = sticky.querySelector(".fz-sticky__price");
    if (line && line.innerHTML !== bar.price) line.innerHTML = bar.price;
    const button = /** @type {HTMLButtonElement | null} */ (sticky.querySelector(".fz-sticky__button"));
    if (button) button.disabled = !bar.can;
    for (const control of sticky.querySelectorAll(".fz-sticky__qty input, .fz-sticky__qty button")) {
      /** @type {HTMLInputElement | HTMLButtonElement} */ (control).disabled = !bar.can;
    }
    const minus = /** @type {HTMLButtonElement | null} */ (sticky.querySelector('[data-fz-sticky-step="-1"]'));
    const count = /** @type {HTMLInputElement | null} */ (sticky.querySelector(".fz-sticky__qty-input"));
    if (bar.can && minus && count) minus.disabled = Number(count.value) <= Number(count.min || 1);
    const words = sticky.querySelector(".fz-sticky__label");
    if (words && words.textContent !== bar.label) words.textContent = bar.label;
  }

  const date = instantMap(section, "ship")?.[id];
  if (typeof date === "string") {
    for (const span of section.querySelectorAll("[data-fz-ship-date]")) {
      if (span.innerHTML !== date) span.innerHTML = date;
    }
  }

  const url = new URL(window.location.href);
  if (url.searchParams.get("variant") !== id) {
    url.searchParams.set("variant", id);
    window.history.replaceState(window.history.state, "", url.toString());
  }

  // The picture: the slide whose slide-id is the variant's media, if this
  // gallery holds it (a colour-keyed gallery, from 5 Oct, may not: then the
  // fetched one brings it, with the fade).
  const gallery = /** @type {any} */ (section.querySelector("media-gallery"));
  const slideshow = gallery && (gallery.slideshow || gallery.querySelector("slideshow-component"));
  if (variant.media && slideshow && typeof slideshow.select === "function") {
    const slide = slideshow.querySelector(`slideshow-slide[slide-id="${variant.media}"]`);
    if (slide) {
      const image = /** @type {HTMLImageElement | null} */ (slide.querySelector("img"));
      if (image) {
        image.loading = "eager";
        image.fetchPriority = "high";
        const colourIndex = optionControls(picker).findIndex((c) => c.querySelector(".fz-swatch"));
        if (colourIndex >= 0) standIn(section, image, colourIndex, variant.options[colourIndex]);
      }
      slideshow.select({ id: String(variant.media) }, undefined, { animate: false });
      return true;
    }
  }
  return false;
}

/*
 * THE PICTURE BEFORE THE TAP. The slide for a colour is in the gallery from
 * the start but lazy, so selecting it at once still waited for its image:
 * the UX journey (26 Sep) measured the image swap at 440-490 ms median, 2.9 s
 * at worst, the slowest part of a switch. On intent -- a pointer over a
 * swatch or size, a press (a phone's ~100 ms before the click), keyboard
 * focus -- the slide the tap would show is made eager and decoded, so it is
 * ready when the tap selects it. Nothing is fetched for a colour nobody
 * reaches for, and nothing on load, so the LCP is untouched.
 * @param {Element} input
 */
export function warmPicture(input) {
  const picker = input.closest("variant-picker");
  const section = input.closest(".shopify-section");
  const variants = picker && instantMap(picker, "variants");
  if (!section || !Array.isArray(variants)) return null;
  const index = optionControls(picker).indexOf(/** @type {Element} */ (input.closest("fieldset")));
  if (index < 0) return null;
  const chosen = chosenValues(picker);
  chosen[index] = /** @type {HTMLInputElement} */ (input).value;
  const variant =
    findVariant(variants, chosen) ||
    variants.find((v) => v.options[index] === /** @type {HTMLInputElement} */ (input).value && v.media);
  if (!variant || !variant.media) return null;
  const image = /** @type {HTMLImageElement | null} */ (
    section.querySelector(`slideshow-slide[slide-id="${variant.media}"] img`)
  );
  if (!image || image.dataset.fzWarm) return image;
  image.dataset.fzWarm = "";
  image.loading = "eager";
  image.fetchPriority = "high";
  if (typeof image.decode === "function") image.decode().catch(() => {});
  return image;
}

/**
 * THE COLOUR AT ONCE, EVEN BEFORE ITS PICTURE. The dev re-check (26 Sep)
 * still measured half a second for the picture, the one thing a switch
 * waits on. The swatch the shopper just tapped already holds that picture,
 * small and loaded, so it is painted as the slide's background (in the
 * slide's own fit) until the full image arrives, then taken off. It costs no
 * byte: the swatch's file is in the cache.
 * @param {Element} section
 * @param {HTMLImageElement | null} image - the slide's image
 * @param {number} optionIndex - the colour option's place among optionControls()
 * @param {string} value - the chosen colour
 */
function standIn(section, image, optionIndex, value) {
  if (!image || (image.complete && image.naturalWidth > 0)) return;
  const picker = section.querySelector("variant-picker");
  const fieldset = picker && optionControls(picker)[optionIndex];
  const input =
    fieldset &&
    [...fieldset.querySelectorAll("input")].find((i) => /** @type {HTMLInputElement} */ (i).value === value);
  const swatch = /** @type {HTMLImageElement | null} */ (
    input && input.closest("label")?.querySelector(".fz-swatch__image")
  );
  const source = swatch && (swatch.currentSrc || swatch.src);
  if (!source || !(swatch.complete && swatch.naturalWidth > 0)) return;
  image.style.backgroundImage = `url("${source.replace(/"/g, "%22")}")`;
  image.style.backgroundSize = getComputedStyle(image).objectFit === "contain" ? "contain" : "cover";
  image.style.backgroundPosition = "center";
  image.style.backgroundRepeat = "no-repeat";
  const clear = () => {
    image.style.removeProperty("background-image");
    image.style.removeProperty("background-size");
    image.style.removeProperty("background-position");
    image.style.removeProperty("background-repeat");
  };
  image.addEventListener("load", clear, { once: true });
  image.addEventListener("error", clear, { once: true });
}

/*
 * THE REST OF THE GALLERY, AFTER THE PAGE (A7 perf, 2026-10-08). The gallery
 * renders only its first slide eager (snippets/fz-gallery-content.liquid): with
 * all of them eager, nine pictures and a poster downloaded beside the LCP
 * picture and it finished last. Lazy alone would cost the swipe, because the
 * scroller clips the other slides and Chromium fetches a clipped lazy image
 * only when it is scrolled into view (measured offline), so the next picture
 * would arrive after the swipe. So once the page has loaded, in idle time,
 * they are fetched at low priority, as the control does with lazysizes'
 * lazypreload. They sit outside the viewport, so they cannot become the LCP.
 * Only the PDP gallery's slides: its zoom dialog and the product cards keep
 * their own loading.
 * @param {ParentNode} [root]
 * @returns {number} how many were woken
 */
export function warmGallery(root = document) {
  const images = /** @type {NodeListOf<HTMLImageElement>} */ (
    root.querySelectorAll('media-gallery[data-fz-block="fz-gallery"] slideshow-slide img[loading="lazy"]')
  );
  for (const image of images) {
    image.fetchPriority = "low";
    image.loading = "eager";
  }
  return images.length;
}
{
  const whenIdle = () =>
    "requestIdleCallback" in window
      ? window.requestIdleCallback(() => warmGallery(), { timeout: 3000 })
      : window.setTimeout(() => warmGallery(), 1500);
  if (document.readyState === "complete") whenIdle();
  else window.addEventListener("load", whenIdle, { once: true });
}

/*
 * THE PAIR AT THE TAP, FROM THE PDP (pdp-add-speed, 10 Oct). Horizon's product
 * form opens the cart drawer as it dispatches the add, before Shopify answers,
 * and the drawer showed the cart as it was until two round trips had passed.
 * A grid card puts what it shows on the add's event for assets/fz-cart.js to
 * paint at once (`fzOptimistic`); this does the same for the PDP's ADD TO CART
 * and the sticky bar's (which presses the same button), from the maps this
 * file already reads: the variant's options as the drawer joins them, its
 * picture from the gallery, its price from the price map. On window, in the
 * capture phase, so it is on the event before the drawer hears it. Not for an
 * add carrying line-item properties (a gift card's recipient): its drawer line
 * shows them, so a painted pair would not be its shape.
 * @param {Element} section
 * @param {string} variantId
 * @param {number} quantity
 */
export function pdpOptimistic(section, variantId, quantity) {
  const picker = section.querySelector("variant-picker");
  const variants = picker ? instantMap(picker, "variants") : null;
  const variant = Array.isArray(variants) ? variants.find((v) => String(v.id) === variantId) : null;
  const heading = section.querySelector(".fz-title");
  let title = section.querySelector(".fz-sticky__title")?.textContent.trim() || "";
  if (!title && heading) {
    const copy = /** @type {Element} */ (heading.cloneNode(true));
    for (const extra of copy.querySelectorAll(".fz-title__ship, .visually-hidden")) extra.remove();
    title = copy.textContent.replace(/\s+/g, " ").trim();
  }
  const options = variant && Array.isArray(variant.options) ? variant.options : [];
  const picture = /** @type {HTMLImageElement | null} */ (
    (variant && variant.media && section.querySelector(`slideshow-slide[slide-id="${variant.media}"] img`)) ||
      section.querySelector("media-gallery img")
  );
  const price = document.createElement("template");
  price.innerHTML = instantMap(section, "price")?.[variantId]?.html || "";
  for (const hidden of price.content.querySelectorAll(".visually-hidden")) hidden.remove();
  const text = (/** @type {string} */ selector) => price.content.querySelector(selector)?.textContent.trim() || "";
  return {
    variantId,
    quantity,
    title,
    variant: options.length === 1 && options[0] === "Default Title" ? "" : options.join("\u2022"),
    image: picture ? picture.currentSrc || picture.src : "",
    now: quantity === 1 ? text(".fz-price__now") : "",
    compare: quantity === 1 ? text(".fz-price__compare") : "",
  };
}
window.addEventListener(
  StandardEvents.cartLinesUpdate,
  (/** @type {any} */ event) => {
    if (event.action !== "add" || event.fzOptimistic) return;
    const form = event.target instanceof Element ? event.target.closest("product-form-component") : null;
    const section = form && form.closest(".shopify-section");
    if (!form || !section || !section.querySelector("fz-sticky-atc, .fz-variants, .fz-title")) return;
    const fields = form.querySelector("form");
    if (fields && [...new FormData(fields)].some(([name, value]) => name.startsWith("properties[") && value !== ""))
      return;
    const line = event.lines && event.lines[0];
    if (!line) return;
    const id = String(line.merchandiseId ?? "");
    try {
      event.fzOptimistic = pdpOptimistic(section, id.slice(id.lastIndexOf("/") + 1), Number(line.quantity) || 1);
    } catch {
      /* nothing painted: the drawer shows the pair when Shopify answers */
    }
  },
  { capture: true },
);

/** @param {Event} event */
function onIntent(event) {
  const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
  const label = target && target.closest(".fz-swatch, .fz-size");
  const input = label && label.querySelector("input");
  if (input) warmPicture(input);
}
for (const type of ["pointerover", "pointerdown", "focusin"]) {
  document.addEventListener(type, onIntent, { passive: true });
}

/*
 * A TAP THAT CHANGES NOTHING STILL ANSWERS (PDP friction, 23 Oct 2026). The
 * 50/50 lost 65 orders to 20 and Clarity counted dead clicks in 17% of the
 * variant's PDP sessions against 2% of the control's. The commonest was a tap
 * on the colour or size ALREADY chosen (Blue three times, Black, Pink, Light
 * Gray, Dark Gray, Medium, Small/Kids): the radio is checked, so no change
 * event, so nothing moved. On Debut the same tap re-runs its swatch handler,
 * which brings back that colour's picture, and a size tap opens the native
 * list. The next commonest was the legend ("Choose your size"), which on
 * Debut is the label of that list. So:
 * - the chosen colour, tapped again, brings back its picture (Debut's
 *   behaviour) and its swatch nudges;
 * - the chosen size, tapped again, nudges;
 * - a legend moves focus to its chosen option and nudges it.
 * The nudge is 220 ms, transform only (an opacity dip under reduced motion),
 * and data-fz-ack marks it on the face for as long as it runs.
 */
const NUDGE_MS = 220;

/** @param {Element | null} face */
export function nudge(face) {
  if (!(face instanceof HTMLElement)) return false;
  face.setAttribute("data-fz-ack", "");
  window.clearTimeout(Number(face.dataset.fzAckTimer));
  face.dataset.fzAckTimer = String(window.setTimeout(() => face.removeAttribute("data-fz-ack"), NUDGE_MS + 40));
  if (typeof face.animate === "function") {
    const frames = reducedMotion()
      ? [{ opacity: 1 }, { opacity: 0.55, offset: 0.35 }, { opacity: 1 }]
      : [{ transform: "scale(1)" }, { transform: "scale(0.95)", offset: 0.35 }, { transform: "scale(1)" }];
    face.animate(frames, { duration: NUDGE_MS, easing: EASE_OUT });
  }
  return true;
}

/** The face a shopper sees for an option's radio: the swatch circle or the size button. */
function faceOf(/** @type {Element | null} */ input) {
  const label = input && input.closest(".fz-swatch, .fz-size");
  return label && label.querySelector(".fz-swatch__circle, .fz-size__button");
}

/**
 * The chosen colour's picture, brought back into view: the slide the variant
 * map names, as showVariant() selects it on a change.
 * @param {Element} section
 */
export function showChosenPicture(section) {
  const picker = section.querySelector("variant-picker");
  if (!picker) return false;
  const variant = findVariant(instantMap(picker, "variants"), chosenValues(picker));
  const gallery = /** @type {any} */ (section.querySelector("media-gallery"));
  const slideshow = gallery && (gallery.slideshow || gallery.querySelector("slideshow-component"));
  if (!variant || !variant.media || !slideshow || typeof slideshow.select !== "function") return false;
  if (!slideshow.querySelector(`slideshow-slide[slide-id="${variant.media}"]`)) return false;
  slideshow.select({ id: String(variant.media) }, undefined, { animate: !reducedMotion() });
  return true;
}

/** @param {MouseEvent} event */
export function onPickerTap(event) {
  const target = event.target instanceof Element ? event.target : null;
  const picker = target && target.closest("variant-picker");
  if (!picker || picker.closest("product-card")) return;
  const section = picker.closest(".shopify-section");
  if (!section) return;

  const legend = target.closest("legend.fz-variants__legend");
  if (legend) {
    const fieldset = legend.closest("fieldset");
    const input = /** @type {HTMLInputElement | null} */ (
      fieldset && (fieldset.querySelector("input:checked") || fieldset.querySelector("input:not([disabled])"))
    );
    if (!input) return;
    input.focus({ preventScroll: true });
    nudge(faceOf(input));
    return;
  }

  // A tap on the label (the face), not the radio's own synthetic click: the
  // radio still holds its state from BEFORE this tap, so checked means "again".
  const label = target.closest(".fz-swatch, .fz-size");
  const input = /** @type {HTMLInputElement | null} */ (label && label.querySelector("input.fz-variants__input"));
  if (!input || target === input || !input.checked) return;
  if (label.classList.contains("fz-swatch")) showChosenPicture(section);
  nudge(faceOf(input));
}
document.addEventListener("click", onPickerTap);

/*
 * THE ZOOM VIEW CLOSES ON A TAP ON A PHONE TOO. Horizon closes it when the
 * picture is clicked (on:click="/close" on each <li>), but on a phone its
 * drag-zoom-wrapper calls preventDefault() on touchstart, which cancels the
 * click: a single tap did nothing, only a double tap zoomed, and Clarity
 * logged the tap as a dead click on .product-media-container. So a single tap
 * that did not move closes it, once the double-tap window (Horizon's 300 ms)
 * has passed without a second tap, and only while the picture is not zoomed
 * in (then a tap belongs to the pan). Desktop never reaches this: no touches.
 */
const ZOOM_TAP_WINDOW = 320;
const ZOOM_TAP_SLOP = 10;
/** @type {{ x: number, y: number, t: number, moved: boolean, wrapper: HTMLElement } | null} */
let zoomTap = null;
let zoomTapTimer = 0;

/** @param {HTMLElement} wrapper */
export function zoomScale(wrapper) {
  const value = parseFloat(wrapper.style.getPropertyValue("--drag-zoom-scale"));
  return Number.isFinite(value) ? value : 1;
}

/** @param {TouchEvent} event */
function onZoomTouchStart(event) {
  const target = event.target instanceof Element ? event.target : null;
  const wrapper = target && target.closest("dialog[open] .dialog-zoomed-gallery drag-zoom-wrapper");
  if (zoomTapTimer) {
    // A second tap inside the window is Horizon's double tap (zoom): not a close.
    window.clearTimeout(zoomTapTimer);
    zoomTapTimer = 0;
    zoomTap = null;
    return;
  }
  if (!wrapper || event.touches.length !== 1) {
    zoomTap = null;
    return;
  }
  const touch = event.touches[0];
  zoomTap = {
    x: touch.clientX,
    y: touch.clientY,
    t: event.timeStamp,
    moved: false,
    wrapper: /** @type {HTMLElement} */ (wrapper),
  };
}

/** @param {TouchEvent} event */
function onZoomTouchMove(event) {
  if (!zoomTap) return;
  const touch = event.touches[0];
  if (!touch || Math.hypot(touch.clientX - zoomTap.x, touch.clientY - zoomTap.y) > ZOOM_TAP_SLOP) zoomTap.moved = true;
}

/** @param {TouchEvent} event */
function onZoomTouchEnd(event) {
  const tap = zoomTap;
  zoomTap = null;
  if (!tap || tap.moved || event.touches.length || event.timeStamp - tap.t > 500) return;
  zoomTapTimer = window.setTimeout(() => {
    zoomTapTimer = 0;
    const dialog = tap.wrapper.closest("dialog");
    const zoom = /** @type {any} */ (tap.wrapper.closest("zoom-dialog"));
    if (!dialog || !dialog.open || !zoom || typeof zoom.close !== "function") return;
    if (zoomScale(tap.wrapper) > 1.01) return;
    zoom.close();
  }, ZOOM_TAP_WINDOW);
}
document.addEventListener("touchstart", onZoomTouchStart, { passive: true, capture: true });
document.addEventListener("touchmove", onZoomTouchMove, { passive: true, capture: true });
document.addEventListener("touchend", onZoomTouchEnd, { passive: true, capture: true });

/*
 * THE BENEFIT PILLS ANSWER WITH THEIR REASONS. Grey rounded pills read as
 * buttons, and "Non-slip grips" was tapped as one. The section below the buy
 * box ("What's so great about The Foozie?") is where each is explained, so a
 * tap on a pill scrolls there. Debut's icon rows were plain text; this is a
 * labelled improvement (likeness-improvements/pdp-friction.json).
 */
document.addEventListener("click", (event) => {
  const pill = event.target instanceof Element ? event.target.closest(".fz-benefits__pill") : null;
  const section = pill && pill.closest(".shopify-section");
  if (!pill || !section) return;
  const features = document.querySelector(".fz-features");
  if (!features) return;
  features.scrollIntoView({ behavior: reducedMotion() ? "instant" : "smooth", block: "start" });
});

/*
 * The tap. Horizon's picker checks the radio and dispatches product:select
 * in the same task, before its fetch, so the chosen variant is shown here at
 * once. Then the gallery swap: Horizon replaces <media-gallery> wholesale
 * when the fetch lands, and it opens on the chosen picture. If that picture
 * is already the one shown, the swap is invisible and gets no fade (a fade
 * over the same picture reads as a flicker); only when it could not be shown
 * here does CSS fade the new gallery in (@starting-style), never on first
 * paint, which would delay the LCP.
 */
document.addEventListener(StandardEvents.productSelect, (/** @type {any} */ event) => {
  const section = event.target instanceof Element ? event.target.closest(".shopify-section") : null;
  if (!section || !section.querySelector("fz-sticky-atc, .fz-variants")) return;
  if (event.target instanceof Element && event.target.closest("product-card")) return;
  let pictureShown = false;
  try {
    pictureShown = showVariant(section);
  } catch (error) {
    console.warn("[fz-product] instant variant", error);
  }
  // Horizon swaps in the chosen colour's gallery when the fetch lands, its
  // slides lazy again: wake them once it is in (warmGallery, above).
  Promise.resolve(event.promise)
    .catch(() => {})
    .finally(() => window.setTimeout(() => warmGallery(section), 600));
  if (pictureShown) return;
  section.setAttribute("data-fz-swapping", "");
  Promise.resolve(event.promise)
    .catch(() => {})
    .finally(() => window.setTimeout(() => section.removeAttribute("data-fz-swapping"), 400));
});
